// Librairies
import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';

// Services
import { CompanyService } from 'src/app/services/api/companies/company.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { LocalStorageService } from 'src/app/services/core/local-storage.service';

// Interfaces
import { CompanyLight } from 'src/app/interfaces/api/company';

const MAX_VISITED = 5;

const toLight = ({ id, name, logo, allProjects }: CompanyLight): CompanyLight => ({ id, name, logo, allProjects });

// Current company shown in the header: a display context only, it filters nothing.
@Injectable({
    providedIn: 'root'
})
export class CompanyStore {
    // Services
    private readonly companyService = inject(CompanyService);
    private readonly authService = inject(AuthService);
    private readonly localStorage = inject(LocalStorageService);

    // Signals
    private readonly _mine = signal<CompanyLight[]>([]);
    readonly mine = this._mine.asReadonly();

    private readonly _current = signal<CompanyLight | null>(null);
    readonly current = this._current.asReadonly();

    private readonly _visited = signal<CompanyLight[]>([]);
    readonly visited = this._visited.asReadonly();

    // False until /companies/mine answered: `current` is not settled yet.
    private readonly _ready = signal(false);
    readonly ready = this._ready.asReadonly();

    // Profile menu: a client's company; for the CAD.42 team, CAD.42 then the last visited companies.
    readonly menuCompanies = computed(() => {
        const mine = this._mine();
        if (!this.authService.isPrivileged()) return mine;

        const mineIds = new Set(mine.map(company => company.id));
        return [...mine, ...this._visited().filter(company => !mineIds.has(company.id))];
    });

    // Variables
    private userId = '';

    // Only the user id: other user updates (e.g. projectsVisited) must not reload.
    private readonly currentUserId = computed(() => this.authService.currentUser().id);

    constructor() {
        effect(() => {
            const userId = this.currentUserId();
            untracked(() => userId ? this.load(userId) : this.reset());
        });
    }

    load(userId: string = this.userId): void {
        this.userId = userId;
        this._ready.set(false);
        this._current.set(this.localStorage.getItem<CompanyLight>(this.key('current')));
        this._visited.set(this.localStorage.getItem<CompanyLight[]>(this.key('visited')) ?? []);

        this.companyService.getMyCompanies().subscribe({
            next: mine => {
                this._mine.set(mine);
                this.resolveCurrent(mine);
                this._ready.set(true);
            },
            error: () => {
                this.reset();
                this._ready.set(true);
            }
        });
    }

    select(company: CompanyLight): void {
        const light = toLight(company);
        this.setCurrent(light);

        if (this.authService.isPrivileged() && !light.allProjects)
            this.setVisited([light, ...this._visited().filter(visited => visited.id !== light.id)].slice(0, MAX_VISITED));
    }

    // After a rename or a logo change.
    refresh(company: CompanyLight): void {
        const light = toLight(company);
        if (this._current()?.id === light.id) this.setCurrent(light);
        this._mine.update(mine => mine.map(item => item.id === light.id ? light : item));
        if (this._visited().some(visited => visited.id === light.id))
            this.setVisited(this._visited().map(visited => visited.id === light.id ? light : visited));
    }

    // After a deletion.
    forget(companyId: string): void {
        this._mine.update(mine => mine.filter(company => company.id !== companyId));
        this.setVisited(this._visited().filter(company => company.id !== companyId));
        if (this._current()?.id === companyId) this.setCurrent(this._mine()[0] ?? null);
    }

    private resolveCurrent(mine: CompanyLight[]): void {
        const stored = this._current();
        const fallback = mine[0] ?? null;

        // A client may only see their own company.
        if (!this.authService.isPrivileged()) {
            this.setCurrent(mine.find(company => company.id === stored?.id) ?? fallback);
            return;
        }

        if (!stored || stored.id === fallback?.id) {
            this.setCurrent(fallback);
            return;
        }

        // The CAD.42 team may be on any company: check it still exists.
        this.companyService.getCompanyTenants(stored.id).subscribe({
            next: company => this.setCurrent(toLight(company)),
            error: () => {
                this.forget(stored.id);
                this.setCurrent(fallback);
            }
        });
    }

    private reset(): void {
        this.userId = '';
        this._ready.set(false);
        this._mine.set([]);
        this._current.set(null);
        this._visited.set([]);
    }

    private setCurrent(company: CompanyLight | null): void {
        this._current.set(company);
        if (!this.userId) return;
        if (company) this.localStorage.setItem(this.key('current'), company);
        else this.localStorage.removeItem(this.key('current'));
    }

    private setVisited(visited: CompanyLight[]): void {
        this._visited.set(visited);
        if (this.userId) this.localStorage.setItem(this.key('visited'), visited);
    }

    // Per user: two accounts on the same browser keep their own context.
    private key(name: 'current' | 'visited'): string {
        return `company.${name}.${this.userId}`;
    }
}
